import { colors } from '@lmc/common/constants'
import dayjs from 'dayjs'
import calendar from 'dayjs/plugin/calendar'
import { useRef } from 'react'
import styled from 'styled-components'
import AccessRestriction from '../AccessRestriction/AccessRestriction'
import { DropdownMenu as DropdownMenuBase } from '../DropdownMenu'
import ListItem from '../ListItem'

dayjs.extend(calendar)

interface DocumentItemProps {
  data: {
    carePlanName?: string
    createdAt?: Date | string
    id: string
    name: string
    originalFile?: string
    pdf?: string | { url?: string }
  }
  deleteLabel?: string
  onDelete?: (id: string) => void
  onEdit?: (data: DocumentItemProps['data']) => void
  readOnly?: boolean
  type?: string
}

const DropdownMenu = DropdownMenuBase as unknown as React.ComponentType<{
  buttonStyle?: React.CSSProperties
  items: { label: string; onClick: () => void }[]
  menuStyle?: React.CSSProperties
}>

export default function DocumentItem({
  data,
  deleteLabel = 'Delete',
  onDelete,
  onEdit,
  readOnly,
  type
}: DocumentItemProps) {
  const hiddenLinkRef = useRef<HTMLAnchorElement>(null)

  const handleView = () => hiddenLinkRef.current?.click()
  const handleEdit = () => onEdit?.(data)
  const handleDelete = () => onDelete?.(data.id)

  const daysDiff = dayjs().diff(data.createdAt, 'days')
  const displayedTime =
    daysDiff <= 7
      ? dayjs(data.createdAt).calendar()
      : `Added ${daysDiff} days ago`

  const label =
    data.name.length > 100 ? `${data.name.substring(0, 100)}...` : data.name

  return (
    <ListItem
      svgIcon="document"
      iconColor={colors.darkblue}
      inlineChildren
      label={label}
      multiSelect
      onSelect={type === 'edit' ? handleEdit : handleView}
      subLabel={displayedTime}
      carePlanName={data.carePlanName}
      iconSize={24}
      size={40}
      hasBorder
    >
      <ActionsContainer>
        <AccessRestriction>
          {!readOnly && (
            <DropdownMenu
              items={[
                { label: 'View', onClick: handleView },
                { label: 'Edit', onClick: handleEdit },
                { label: deleteLabel, onClick: handleDelete }
              ]}
              buttonStyle={dropdownStyles.menuIcon}
              menuStyle={dropdownStyles.menuContainer}
            />
          )}
        </AccessRestriction>
        <HiddenLink
          href={
            data.originalFile ||
            (typeof data.pdf === 'string' ? data.pdf : data.pdf?.url)
          }
          ref={hiddenLinkRef}
          rel="noreferrer"
          target="_blank"
        >
          {' '}
        </HiddenLink>
      </ActionsContainer>
    </ListItem>
  )
}

const ActionsContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`

const HiddenLink = styled.a`
  left: -9999px;
  position: absolute;
`

const dropdownStyles = {
  menuContainer: {
    position: 'absolute' as const,
    top: 25,
    right: '-13px'
  },
  menuIcon: {
    alignItems: 'center',
    display: 'flex'
  }
}
